<template>
  <div>
    <div class="mb-4">
      <FormButton 
        v-if="commentsEnabled && !postComment.enabled && userHasCreatePermission(PermissionTypes.CommunicationPermissionTypes.comment) && canPostComments"
        class="btn btn-med btn-primary float-right"
        @click="onNewComment">
        <i class="far fa-comment"></i> Add Comment
      </FormButton>

      <ReportFilterPopover
        class="float-right mr-2"
        headerTitle="Activity Log Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :filterActive="hasFilterActive"
        @apply="applyLogFilter"
        @reset="resetLogFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Filter">
            <ReportDateRangeSelect
              :startDate="editingLogFilter.startDate"
              :endDate="editingLogFilter.endDate"
              @updateStartDate="editingLogFilter.startDate = $event"
              @updateEndDate="editingLogFilter.endDate = $event"
            />
          </ReportFilterGroup>
          <ReportFilterGroup label="Activity Log Type">
            <ReportMultiSelect :options="ActivityLogTypeLookup" v-model="editingLogFilter.logTypeList" />

          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div v-if="appliedLogFilter.startDate || appliedLogFilter.endDate">
            <i class="far fa-calendar mr-2"></i>{{appliedLogFilter.startDate | formatDate}} - {{appliedLogFilter.endDate | formatDate}}
          </div>
          <div v-if="appliedLogFilter.logTypeList && appliedLogFilter.logTypeList.length > 0">
            <i class="far fa-clipboard-list mr-2"></i>{{appliedLogFilter.logTypeList.length}} Log Type{{displayCountPlural(appliedLogFilter.logTypeList.length)}}
          </div>
        </template>
      </ReportFilterPopover>

      <!-- Search: Disabled as searching nested activity item comments is proving really complex. Not 100% needed at this stage -->
      <ReportFilterSearchBar class="float-right mr-2" v-model="filterSearchText" v-if="false" />

      <slot name="title" />
    </div>
    <ActivityCommentEditor
      ref="postCommentEditor"
      v-if="postComment.enabled && commentsEnabled"
      v-model="postComment.commentData"
      :linkType="linkType"
      :linkId="linkId"
      @post="onPostRootComment()"
      @cancel="postComment.enabled=false">
    </ActivityCommentEditor>

    <Scroller height="600">
      <Spinner v-if="loading" />
      <div class="activity-group-title text-center" v-if="hasFilterActive">Showing Filtered Logs</div>

      <div class="activity-stream">
        <div v-for="(group, groupIndex) in groupLogList" :key="`group-${groupIndex}`">
          <div class="activity-group-title">{{group.name}}</div>

          <div class="activity-item" v-for="activity in group.items" :key="activity.id">
            <ActivityLogItem 
              :item="activity"
              :linkType="linkType"
              :linkId="linkId"
              :rootItem="true"
              :commentsEnabled="commentsEnabled"
              @reply="onCommentReplied()"
              @delete="onCommentDelete()"
              />
          </div>
        </div>
      </div>

      <div v-if="hasMoreLogItems" class="show-more-container">
        <FormButton class="btn btn-default btn-med" @click="loadMoreLogs" :loading="loadingMore" :disabled="loadingMore">Show More</FormButton>
      </div>
    </Scroller>
  </div>
</template>

<style scoped>
.show-more-container {
  width: 100%;
  text-align: center;
  padding: 5px 0px;
}
</style>

<script>
import _ from 'lodash'

import SlimScroll from '@/components/slimScroll'
import Avatar from '@/components/avatar'
import ActivityCommentEditor from '@/components/activityCommentEditor'
import ActivityLogItem from '@/components/activityLogItem'
import DateUtils from '@/utils/dateUtils'

import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import ReportFilterSearchBar from '@/components/reporting/reportFilterSearchBar'
import ReportMultiSelect from '@/components/reporting/reportMultiSelect'

import { ActivityItemTypes, ActivityLogTypes, ActivityLogTypeLookup, LinkTypes } from '@/store/types'

export default {
  name: 'ActivityLogStream',
  components: {
    SlimScroll,
    ActivityCommentEditor,
    ActivityLogItem,
    ReportDateRangeSelect,
    ReportFilterPopover,
    ReportFilterGroup,
    ReportFilterSearchBar,
    ReportMultiSelect
  },
  props: {
    url: String,
    linkType: String,
    linkId: [Number, String],
    commentsEnabled: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    canPostComments() {
      // Cannot post comments if no link type is set
      // Usually due to collated activity streams
      if(this.linkType == null)
        return false
      return true
    },
    hasMoreLogItems() {
      if(this.loading)
        return false
      return (this.paging.currentOffset + this.paging.currentLogSize < this.paging.recordsTotal)
    },
    hasFilterActive() {
      return this.appliedLogFilter.startDate != '' || this.appliedLogFilter.endDate != '' || this.appliedLogFilter.logTypeList.length > 0
    }
  },
  data() {
    return {
      loading: true,
      loadingMore: false,
      logList: [],
      paging: {
        currentOffset: 0,
        currentLogSize: 0,
        pageSize: 50,
        recordsTotal: 0
      },
      groupLogList: [],
      postComment: {
        enabled: false,
        commentData: {}
      },
      postReply: {
        enabled: false,
        activity: null,
        commentData: {}
      },

      // Filters
      defaultLogFilter: {    
        startDate: '',
        endDate: '',
        logTypeList: []
      },
      editingLogFilter: {},
      appliedLogFilter: {},

      // Search
      filterSearchText: ''
    };
  },
  created() {
    this.debouncedUpdate = _.debounce(this.updateFilters, 250)

    this.ActivityLogTypeLookup = ActivityLogTypeLookup
  },
  mounted() {
    this.editingLogFilter = _.clone(this.defaultLogFilter)
    this.appliedLogFilter = _.clone(this.defaultLogFilter)

    this.loading = true
    this.requestData();
  },
  watch: {
    url: function(newVal, oldVal) {
      this.loading = true
      this.resetPaging()
      this.requestData()
    },
    filterSearchText: function() {
      this.debouncedUpdate()
    }
  },
  methods: {
    reloadData() {
      this.loading = true
      this.resetPaging()
      this.requestData()
    },
    requestData() {
      if (!this.url)
        return

      var params = {
        start: this.paging.currentOffset,
        length: this.paging.pageSize,
        //search: this.filterSearchText     // Disabled until 100% solved for searching nested comments on server side
      }
      if(this.appliedLogFilter.startDate)
        params['filter[rootItemDate][gte]'] = this.appliedLogFilter.startDate
      if(this.appliedLogFilter.endDate)
        params['filter[rootItemDate][lte]'] = this.appliedLogFilter.endDate

      if(this.appliedLogFilter.logTypeList.length > 0)
        params['filter[activityType]'] = this.appliedLogFilter.logTypeList.join(',')

      var activityURL = this.url + '?' + this.buildQueryParams(params)

      this.$api
        .get(activityURL)
        .then(response => {
          // If starting offset is zero, replace the log list with the data
          // Otherwise append to the end (ie, 'show more')
          let resetLog = (this.paging.currentOffset == 0)

          if(resetLog) {
            this.logList = response.data.data
            this.groupLogList = []
          } else {
            this.logList = _.concat(this.logList, response.data.data)
          }

          this.paging.currentLogSize = this.logList.filter(log => log.parentActivityItemId == null).length
          this.paging.recordsTotal = response.data.recordsTotal

          var newGroupLogList = []

          // Add some processing & calculations
          let currentGroup = null;

          response.data.data.forEach(log => {
            let dateGroupName = DateUtils.getDateDifferenceNamePast(
              new Date(log.createdAt)
            );
            if (!currentGroup || currentGroup.name != dateGroupName) {
              if (currentGroup)
                newGroupLogList.push(currentGroup)
              currentGroup = { name: dateGroupName, items: [] }
            }
            if (currentGroup)
              currentGroup.items.push(log)
          })
          if (currentGroup && currentGroup.items.length > 0)
            newGroupLogList.push(currentGroup)

          this.groupLogList = _.concat(this.groupLogList, newGroupLogList)
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    resetPaging() {
      this.paging.currentOffset = 0
      this.paging.recordsTotal = 0
      this.paging.currentLogSize = 0
    },
    updateFilters() {
      this.resetPaging()
      this.reloadData()
    },

    activityUserImage(activity) {
      if (activity.user && activity.user.profileURL != "")
        return activity.user.profileURL;
      return "/glass/img/user-overview-male.png";
    },
    activityItemIcon(activity) {
      if(activity.type == ActivityItemTypes.comment)
        return 'fa-comment'
      
      if(activity.type == ActivityItemTypes.activityLog) {
        switch (activity.activityType) {
          case ActivityLogTypes.created:
            return "fa-plus";
          case ActivityLogTypes.updated:
            return "fa-pencil";
          case ActivityLogTypes.deleted:
            return "fa-trash";
          case ActivityLogTypes.archived:
            return "fa-archive";
          case ActivityLogTypes.restored:
            return "fa-trash-restore";
          case ActivityLogTypes.status:
            return "fa-info-circle";
          case ActivityLogTypes.action:
            return "fa-tasks";
          case ActivityLogTypes.error:
            return "fa-exclamation-triangle";
          case ActivityLogTypes.approved:
            return "fa-thumbs-up";
          case ActivityLogTypes.denied:
            return "fa-thumbs-down";
          case ActivityLogTypes.emailed:
            return "fa-paper-plane";
        }
      }
      return "fa-pencil";
    },
    activityItemBadgeClass(activity) {
      if(activity.type == ActivityItemTypes.comment)
        return 'bg-primary'
      
      if(activity.type == ActivityItemTypes.activityLog) {
        switch (activity.activityType) {
          case ActivityLogTypes.created:
            return "bg-primary";
          case ActivityLogTypes.updated:
            return "bg-default";
          case ActivityLogTypes.deleted:
            return "bg-danger";
          case ActivityLogTypes.archived:
            return "bg-warning";
          case ActivityLogTypes.restored:
            return "bg-success";
          case ActivityLogTypes.status:
            return "bg-success";
          case ActivityLogTypes.action:
            return "bg-action";
          case ActivityLogTypes.error:
            return "bg-danger";
          case ActivityLogTypes.approved:
            return "bg-success";
          case ActivityLogTypes.denied:
            return "bg-danger";
          case ActivityLogTypes.emailed:
            return "bg-success";
        }
      }
      return "";
    },
    onNewComment() {
      this.postComment.enabled = true
      this.$nextTick(() => {
        this.$refs.postCommentEditor.select()
      })
    },
    onPostRootComment() {
      this.postComment.enabled = false
      this.requestData()
    },
    itemIsComment(activity) {
      return activity.type == ActivityItemTypes.comment
    },
    replyActivity(activity) {
      this.postReply.enabled = true
      this.postReply.activity = activity
    },
    onCommentReplied() {
      this.reloadData()
    },
    onCommentDelete() {
      this.reloadData()
    },
    loadMoreLogs() {
      this.paging.currentOffset += this.paging.pageSize
      this.loadingMore = true
      this.requestData()
    },
    applyLogFilter() {
      this.appliedLogFilter = _.clone(this.editingLogFilter)

      this.reloadData()
    },
    resetLogFilter() {
      this.editingLogFilter = _.clone(this.defaultLogFilter)
      this.appliedLogFilter = _.clone(this.defaultLogFilter)

      this.reloadData()
    },
    
  }
};
</script>